<script setup lang="ts">
import { useField } from 'vee-validate'
import { onMounted, ref, toRef } from 'vue'
import ValidationMessage from '@/components/form/ValidationMessage.vue'
import { useGoogleMapsAutocomplete } from '@/composables/google-maps-autocomplete'
import type { Address } from '@/utils'
import { isNullOrUndefined } from '@/utils'

const props = defineProps({
  name: {
    type: String,
    required: true,
  },
  required: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  labelClass: {
    type: String,
    default: '',
  },
  cssClass: {
    type: String,
    default: '',
  },
  validation: {
    type: Boolean,
    default: true,
  },
  pointValue: {
    type: String,
    default: '',
  },
  borderDanger: {
    type: Boolean,
    default: false,
  },
  unstyled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits<{
  (e: 'addressChange', address: Address): void
  (e: 'change', value: string): void
  (e: 'blur'): void
}>()

const nameRef = toRef(props, 'name')

const {
  value: inputValue,
  errorMessage,
  handleChange,
  setValue: setFieldValue,
  resetField,
} = useField(nameRef)

const hasValue = ref(!!props.pointValue)
const autocompleteInput = ref<HTMLInputElement>()
const autocomplete = useGoogleMapsAutocomplete()

const reset = () => resetField({ value: '' })
const setValue = (value: string) => setFieldValue(value)

defineExpose({
  reset,
  setValue,
})

onMounted(async () => {
  await autocomplete.initialize()
})

async function onInput(e?: Event) {
  handleChange(e)
  hasTextOrNumber(inputValue.value)

  emit('change', inputValue.value as string)

  if (inputValue.value && String(inputValue.value).length > 2)
    await autocomplete.fetchSuggestions(String(inputValue.value))
  else
    autocomplete.hideSuggestions()
}

async function selectSuggestion(suggestion: any) {
  const result = await autocomplete.selectSuggestion(suggestion)

  setValue(result.text)

  if (result.address)
    emit('addressChange', result.address)
}

function handleKeydown(e: KeyboardEvent) {
  if (autocomplete.handleKeydown(e) && e.key === 'Enter') {
    const selectedSuggestion = autocomplete.getSelectedSuggestion()
    if (selectedSuggestion)
      selectSuggestion(selectedSuggestion)
  }
}

function handleBlur() {
  // Pressing a suggestion keeps the focus in the input (mousedown.prevent on the list), so this
  // only runs when the user really leaves the field; the delay is a safety net for browsers that blur anyway.
  setTimeout(() => {
    autocomplete.hideSuggestions()
  }, 150)
  emit('blur')
}

function hasTextOrNumber(inputData: unknown) {
  if (isNullOrUndefined(inputData)) {
    hasValue.value = false

    return
  }
  if (typeof inputData === 'string') {
    hasValue.value = inputData.trim() !== ''

    return
  }
  hasValue.value = true
}
</script>

<template>
  <div :class="unstyled ? '' : ['validation-input', { 'has-error': !!errorMessage }]">
    <label
      v-if="label"
      class="form-label d-block"
      :class="[labelClass, { required }]"
      :for="name"
    >
      {{ label }}
    </label>
    <div class="position-relative">
      <input
        ref="autocompleteInput"
        :class="[
          unstyled ? '' : 'form-control d-block user-input',
          cssClass,
          { 'border-danger': !hasValue && borderDanger && !disabled },
        ]"
        :placeholder="placeholder"
        :disabled="disabled"
        :value="pointValue || inputValue"
        @input="onInput"
        @blur="handleBlur"
        @keydown="handleKeydown"
      >
      <!--
        Without mousedown.prevent the input blurs on press and hides the list before a longer click is released, so the click never selects.
        holdSuggestions() keeps a response for newer text from swapping the list under the pointer before the click lands.
      -->
      <div
        v-if="autocomplete.state.showSuggestions && autocomplete.state.suggestions.length > 0"
        class="autocomplete-suggestions position-absolute start-0 end-0 bg-white border border-top-0 overflow-auto shadow-sm"
        @mousedown.prevent="autocomplete.holdSuggestions()"
      >
        <div
          v-for="(item, index) in autocomplete.state.suggestions"
          :key="index"
          class="suggestion-item py-2 px-3"
          :class="{ selected: index === autocomplete.state.selectedIndex }"
          @click="selectSuggestion(item)"
        >
          {{ item.displayText }}
        </div>
      </div>
    </div>

    <ValidationMessage v-if="validation" :error-message="errorMessage" :unstyled="unstyled" />
  </div>
</template>

<style scoped>
.autocomplete-suggestions {
  top: 100%;
  max-height: 200px;
  z-index: 1000;
  border-radius: 0 0 4px 4px;
}

input.user-input.free-quote-address-input {
  border-color: #E5E8ED;
}

input.user-input.free-quote-address-input:focus {
  border-color: #E5E8ED;
}

.suggestion-item {
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
}

.suggestion-item:last-child {
  border-bottom: none;
}

.suggestion-item:hover,
.suggestion-item.selected {
  background-color: #f8f9fa;
}
</style>
